import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Slider } from "./Slider";
import { Spinner } from "./Spinner";
import { PauseIcon, PlayIcon, RefreshIcon, WaveformIcon } from "./icons";

export interface AudioPlayerProps {
  src?: string;
  label?: string;
  /** Optional secondary text beside a compact player's title. */
  description?: string;
  disabled?: boolean;
  preload?: "none" | "metadata" | "auto";
  className?: string;
  /** A neutral horizontal result row with an inline title and duration. */
  compact?: boolean;
  /** Compact rows can expose asset actions; the full player retains speed control. */
  actions?: ReactNode;
}

const PLAY_EVENT = "afterform:audio-play";
const SPEEDS = [1, 1.25, 1.5, 2];

function timeLabel(seconds: number) {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor(value / 60) % 60;
  const remainder = String(value % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${remainder}` : `${minutes}:${remainder}`;
}

/** Native audio playback with shared controls. Changing the source resets playback. */
export function AudioPlayer(props: AudioPlayerProps) {
  return <AudioPlayerSource key={props.src ?? ""} {...props} />;
}

function AudioPlayerSource({ src, label, description, disabled = false, preload = "metadata", className, compact = false, actions }: AudioPlayerProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ended, setEnded] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const name = label ?? t("audio.player.label", { defaultValue: "Audio preview" });
  const unavailable = disabled || !src;
  const state = unavailable ? "unavailable" : failed ? "error" : waiting ? "loading" : playing ? "playing" : ended ? "ended" : position > 0 ? "paused" : "ready";

  useEffect(() => {
    const audio = audioRef.current;
    const pauseForOtherPlayer = (event: Event) => {
      if ((event as CustomEvent<HTMLAudioElement>).detail !== audio) audio?.pause();
    };
    window.addEventListener(PLAY_EVENT, pauseForOtherPlayer);
    return () => {
      window.removeEventListener(PLAY_EVENT, pauseForOtherPlayer);
      audio?.pause();
    };
  }, []);

  useEffect(() => {
    if (disabled) audioRef.current?.pause();
  }, [disabled]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || unavailable) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    if (failed) {
      setFailed(false);
      audio.load();
    }
    if (audio.ended) audio.currentTime = 0;
    setWaiting(true);
    try {
      await audio.play();
    } catch (error) {
      if (audioRef.current !== audio) return;
      setWaiting(false);
      if (!(error instanceof Error && error.name === "AbortError")) setFailed(true);
    }
  };

  const playLabel = failed
    ? t("audio.player.retry", { defaultValue: "Retry playback" })
    : playing
      ? t("audio.player.pause", { defaultValue: "Pause audio" })
      : ended
        ? t("audio.player.replay", { defaultValue: "Replay audio" })
        : t("audio.player.play", { defaultValue: "Play audio" });

  return <div role="group" aria-label={name} data-state={state} data-layout={compact ? "compact" : undefined} className={cn("studio-audio-player", className)}>
    <audio ref={audioRef} src={src} preload={preload} hidden
      onLoadedMetadata={event => {
        const value = event.currentTarget.duration;
        setDuration(Number.isFinite(value) ? value : 0);
      }}
      onDurationChange={event => {
        const value = event.currentTarget.duration;
        setDuration(Number.isFinite(value) ? value : 0);
      }}
      onTimeUpdate={event => setPosition(event.currentTarget.currentTime)}
      onPlay={event => {
        if (disabled) { event.currentTarget.pause(); return; }
        setPlaying(true);
        setEnded(false);
        window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: event.currentTarget }));
      }}
      onPlaying={() => { setWaiting(false); setFailed(false); }}
      onWaiting={() => setWaiting(true)}
      onCanPlay={() => setWaiting(false)}
      onPause={() => { setPlaying(false); setWaiting(false); }}
      onEnded={() => { setEnded(true); setPlaying(false); setWaiting(false); }}
      onError={() => { setFailed(true); setPlaying(false); setWaiting(false); }}
      onRateChange={event => setSpeed(event.currentTarget.playbackRate)}
    />
    {!compact ? <div className="studio-audio-player-heading"><WaveformIcon className="size-4 shrink-0 text-media-voice" /><span title={name}>{name}</span></div> : null}
    <div className="studio-audio-player-controls">
      <IconButton tone={compact ? "neutral" : "voice"} size={compact ? "sm" : "md"} className={compact ? "studio-audio-player-play" : "size-11 sm:size-11"} aria-label={playLabel} disabled={unavailable} onClick={() => { void togglePlayback(); }}>
        {waiting ? <Spinner size={18} className="border-white/35 border-t-white" /> : failed || ended ? <RefreshIcon /> : playing ? <PauseIcon /> : <PlayIcon />}
      </IconButton>
      <div className="studio-audio-player-timeline">
        {compact ? <div className="studio-audio-player-summary"><span title={name}>{description ? <><span className="studio-audio-player-name">{name}</span><span className="studio-audio-player-description">{description}</span></> : name}</span><span aria-hidden="true">{position > 0 || playing ? `${timeLabel(position)} / ` : ""}{duration > 0 ? timeLabel(duration) : "--:--"}</span></div> : null}
        <Slider label={t("audio.player.seek", { defaultValue: "Playback position" })} tone={compact ? "neutral" : "audio"} hideLabel showValue={false}
          min={0} max={duration || 1} step="any" value={position} formatValue={timeLabel}
          disabled={unavailable || failed || duration <= 0}
          aria-valuetext={t("audio.player.position", { defaultValue: "{{current}} of {{duration}}", current: timeLabel(position), duration: timeLabel(duration) })}
          onValueChange={value => {
            if (!audioRef.current || duration <= 0) return;
            audioRef.current.currentTime = value;
            setPosition(value);
            setEnded(false);
          }} />
        {!compact ? <div className="studio-audio-player-times" aria-hidden="true"><span>{timeLabel(position)}</span><span>{duration > 0 ? timeLabel(duration) : "--:--"}</span></div> : null}
      </div>
      {compact && actions ? <div className="studio-audio-player-actions">{actions}</div> : <Button variant="ghost" size="sm" className="min-w-11 px-1 text-xs text-ink-soft" disabled={unavailable || failed}
        aria-label={t("audio.player.speed", { defaultValue: "Playback speed: {{speed}}×", speed })}
        onClick={() => {
          if (!audioRef.current) return;
          const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
          audioRef.current.playbackRate = next;
          setSpeed(next);
        }}>{speed}×</Button>}
    </div>
    {failed ? <p role="status" className="studio-audio-player-message text-danger-text">{t("audio.player.error", { defaultValue: "Audio couldn’t play. Try again." })}</p> : null}
    {!src ? <p className="studio-audio-player-message text-ink-muted">{t("audio.playback.unavailable", { defaultValue: "Audio preview unavailable" })}</p> : null}
  </div>;
}
